import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { dashboardAPI, commesseAPI, fattureAPI, timesheetAPI, backupAPI } from '../lib/api';
import { formatCurrency, formatPercent, getStatusLabel } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionsContext';
import { toast } from 'sonner';
import { 
  Briefcase, 
  Building2, 
  Users, 
  TrendingUp, 
  TrendingDown,
  ArrowRight,
  ArrowUpRight,
  Euro,
  Clock,
  Calendar,
  AlertTriangle,
  CheckCircle2,
  FileText,
  Zap,
  Target,
  Activity,
  Download,
  Loader2
} from 'lucide-react';
import { 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  Tooltip, 
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell
} from 'recharts';

// Color palette - Swiss Controller style
const COLORS = {
  primary: '#2563EB',
  success: '#10B981', 
  warning: '#F59E0B',
  danger: '#EF4444',
  muted: '#6B7280',
  dark: '#111827'
};

// Mini Stat Card Component
const StatCard = ({ label, value, change, changeType, icon: Icon, accent = false, valueColor = null }) => (
  <div className={`relative p-4 rounded-sm border ${accent ? 'bg-blue-600 text-white border-blue-600' : 'bg-card border-border'}`}>
    <div className="flex items-start justify-between">
      <div>
        <p className={`text-xs font-medium uppercase tracking-wider ${accent ? 'text-blue-100' : 'text-muted-foreground'}`}>
          {label}
        </p>
        <p className={`text-2xl font-bold font-mono mt-1 ${
          valueColor === 'success' ? 'text-emerald-500' :
          valueColor === 'danger' ? 'text-red-500' :
          accent ? 'text-white' : ''
        }`}>
          {value}
        </p>
        {change !== undefined && (
          <div className={`flex items-center gap-1 mt-1 text-xs ${
            changeType === 'positive' ? 'text-emerald-500' : 
            changeType === 'negative' ? 'text-red-500' : 
            accent ? 'text-blue-200' : 'text-muted-foreground'
          }`}>
            {changeType === 'positive' ? <TrendingUp className="h-3 w-3" /> : 
             changeType === 'negative' ? <TrendingDown className="h-3 w-3" /> : null}
            <span>{change}</span>
          </div>
        )}
      </div>
      {Icon && (
        <div className={`p-2 rounded-sm ${accent ? 'bg-blue-500' : 'bg-muted'}`}>
          <Icon className={`h-4 w-4 ${accent ? 'text-white' : 'text-muted-foreground'}`} />
        </div>
      )}
    </div>
  </div>
);

// Large Metric Card
const MetricCard = ({ title, value, subtitle, progress, icon: Icon, color = 'default' }) => {
  const colorClasses = {
    default: 'border-l-blue-600',
    success: 'border-l-emerald-500',
    warning: 'border-l-amber-500',
    danger: 'border-l-red-500'
  };
  
  return (
    <Card className={`border-l-4 ${colorClasses[color]} rounded-sm`}>
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{title}</span>
          {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
        </div>
        <p className="text-3xl font-bold font-mono">{value}</p>
        {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
        {progress !== undefined && (
          <div className="mt-3">
            <Progress value={progress} className="h-1.5" />
            <p className="text-xs text-muted-foreground mt-1">{progress}% completato</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Alert Item Component
const AlertItem = ({ type, title, description, action, actionLabel }) => {
  const typeConfig = {
    warning: { icon: AlertTriangle, bg: 'bg-amber-50 dark:bg-amber-950/30', border: 'border-amber-200 dark:border-amber-800', text: 'text-amber-700 dark:text-amber-300' },
    success: { icon: CheckCircle2, bg: 'bg-emerald-50 dark:bg-emerald-950/30', border: 'border-emerald-200 dark:border-emerald-800', text: 'text-emerald-700 dark:text-emerald-300' },
    danger: { icon: AlertTriangle, bg: 'bg-red-50 dark:bg-red-950/30', border: 'border-red-200 dark:border-red-800', text: 'text-red-700 dark:text-red-300' }
  };
  const config = typeConfig[type] || typeConfig.warning;
  const Icon = config.icon;
  
  return (
    <div className={`flex items-start gap-3 p-3 rounded-sm border ${config.bg} ${config.border}`}>
      <Icon className={`h-5 w-5 mt-0.5 ${config.text}`} />
      <div className="flex-1 min-w-0">
        <p className={`font-medium text-sm ${config.text}`}>{title}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
      </div>
      {action && (
        <Button variant="ghost" size="sm" onClick={action} className="shrink-0">
          {actionLabel || 'Vedi'}
          <ArrowRight className="h-3 w-3 ml-1" />
        </Button>
      )}
    </div>
  );
};

// Project Row Component - Enhanced
const ProjectRow = ({ project }) => {
  const budget = project.budget_contratto || 0;
  // Costi totali = personale + trasferte + esterni
  const costiTotali = project.kpi?.costi_totali_consuntivo || 0;
  const oreConsuntive = project.kpi?.ore_consuntive || 0;
  const orePreviste = project.kpi?.ore_totali_previste || 0;
  
  // Calcola marginalità attuale: (Budget - Costi Totali) / Budget * 100
  const marginalitaAttuale = budget > 0 ? ((budget - costiTotali) / budget * 100) : 0;
  
  // Avanzamento ore
  const avanzamentoOre = orePreviste > 0 ? (oreConsuntive / orePreviste * 100) : 0;
  
  return (
    <div className="p-4 rounded-sm hover:bg-muted/50 transition-colors group border-b last:border-b-0">
      {/* Top Row: Codice, Nome, Badge, Arrow */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-mono text-xs font-bold text-blue-600">{project.codice_commessa}</span>
            <Badge variant={project.stato === 'attiva' ? 'default' : 'secondary'} className="text-[10px] px-1.5">
              {getStatusLabel(project.stato)}
            </Badge>
          </div>
          <p className="font-medium text-base truncate">{project.nome_progetto}</p>
          {project.cliente_nome && (
            <p className="text-xs text-muted-foreground mt-0.5">{project.cliente_nome}</p>
          )}
        </div>
        <Link to={`/commesse/${project.id}`}>
          <Button variant="ghost" size="icon" className="opacity-0 group-hover:opacity-100 transition-opacity">
            <ArrowUpRight className="h-4 w-4" />
          </Button>
        </Link>
      </div>
      
      {/* Bottom Row: Stats Grid */}
      <div className="grid grid-cols-4 gap-4">
        {/* Budget */}
        <div>
          <p className="text-xs text-muted-foreground mb-0.5">Budget</p>
          <p className="font-mono text-sm font-bold">{formatCurrency(budget)}</p>
        </div>
        
        {/* Costi Attuali */}
        <div>
          <p className="text-xs text-muted-foreground mb-0.5">Costi Attuali</p>
          <p className="font-mono text-sm font-bold">{formatCurrency(costiTotali)}</p>
        </div>
        
        {/* Ore */}
        <div>
          <p className="text-xs text-muted-foreground mb-0.5">Ore</p>
          <div className="flex items-center gap-1.5">
            <span className="font-mono text-sm font-bold">{oreConsuntive.toFixed(0)}</span>
            <span className="text-xs text-muted-foreground">/ {orePreviste.toFixed(0)}</span>
          </div>
          <Progress value={Math.min(avanzamentoOre, 100)} className="h-1 mt-1" />
        </div>
        
        {/* Marginalità Attuale */}
        <div>
          <p className="text-xs text-muted-foreground mb-0.5">Marginalità</p>
          <p className={`font-mono text-sm font-bold ${marginalitaAttuale >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
            {marginalitaAttuale >= 0 ? '+' : ''}{marginalitaAttuale.toFixed(1)}%
          </p>
        </div>
      </div>
    </div>
  );
};

// Dashboard per Risorsa (semplificata)
const RisorsaDashboard = () => {
  const { user } = useAuth();
  const [weekSummary, setWeekSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const today = new Date();
      const monday = new Date(today);
      monday.setDate(today.getDate() - today.getDay() + 1);
      const mondayStr = monday.toISOString().split('T')[0];
      
      const res = await timesheetAPI.getWeekSummary(mondayStr);
      setWeekSummary(res.data);
    } catch (error) {
      console.error('Error loading risorsa dashboard:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6" data-testid="risorsa-dashboard-loading">
        <Skeleton className="h-12 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-28" />)}
        </div>
      </div>
    );
  }

  const oreTotali = weekSummary?.ore_totali || 0;
  const giorniCompleti = weekSummary?.giorni?.filter(g => g.ore_totali >= 8).length || 0;
  const oreTarget = 40;
  const progressOre = Math.min((oreTotali / oreTarget) * 100, 100);

  // Raggruppa ore per commessa
  const orePerCommessa = weekSummary?.giorni?.reduce((acc, giorno) => {
    giorno.entries?.forEach(entry => {
      const key = entry.commessa_codice || 'N/A';
      if (!acc[key]) acc[key] = { codice: key, nome: entry.commessa_nome, ore: 0 };
      acc[key].ore += entry.ore || 0;
    });
    return acc;
  }, {}) || {};

  return (
    <div className="space-y-6" data-testid="risorsa-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <p className="text-sm text-muted-foreground">Bentornato,</p>
          <h1 className="text-3xl font-bold tracking-tight">{user?.nome || 'Utente'}</h1>
        </div>
        <Link to="/timesheet">
          <Button className="bg-blue-600 hover:bg-blue-700" data-testid="vai-timesheet-btn">
            <Clock className="h-4 w-4 mr-2" />
            Registra Ore
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </Link>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard
          label="Ore Settimana"
          value={`${oreTotali}h`}
          change={`${oreTarget - oreTotali}h rimanenti`}
          icon={Clock}
          accent
        />
        <StatCard
          label="Giorni Completi"
          value={giorniCompleti}
          change="su 5 giorni"
          icon={Calendar}
        />
        <StatCard
          label="Completamento"
          value={`${progressOre.toFixed(0)}%`}
          change={progressOre >= 100 ? 'Obiettivo raggiunto!' : 'In corso'}
          changeType={progressOre >= 100 ? 'positive' : undefined}
          icon={Target}
        />
      </div>

      {/* Progress Bar */}
      <Card className="rounded-sm">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Progresso Settimanale</span>
            <span className="font-mono text-sm">{oreTotali}h / {oreTarget}h</span>
          </div>
          <div className="relative">
            <Progress value={progressOre} className="h-3" />
            <div className="absolute top-0 left-1/2 w-0.5 h-3 bg-muted-foreground/30" style={{ transform: 'translateX(-50%)' }} />
          </div>
          <div className="flex justify-between mt-1 text-xs text-muted-foreground">
            <span>0h</span>
            <span>20h</span>
            <span>40h</span>
          </div>
        </CardContent>
      </Card>

      {/* Ore per Commessa */}
      {Object.keys(orePerCommessa).length > 0 && (
        <Card className="rounded-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Distribuzione Ore</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {Object.values(orePerCommessa)
              .sort((a, b) => b.ore - a.ore)
              .map((item) => (
              <div key={item.codice} className="flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs text-blue-600">{item.codice}</span>
                  </div>
                  <p className="text-sm truncate">{item.nome}</p>
                </div>
                <div className="w-32">
                  <Progress value={(item.ore / oreTotali) * 100} className="h-2" />
                </div>
                <span className="font-mono text-sm font-bold w-12 text-right">{item.ore}h</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Main Dashboard Component
export const DashboardPage = () => {
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const [stats, setStats] = useState(null);
  const [commesse, setCommesse] = useState([]);
  const [ricaviSummary, setRicaviSummary] = useState(null);
  const [alertScadenze, setAlertScadenze] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);

  const isRisorsa = user?.ruolo === 'risorsa';
  const isAdmin = user?.ruolo === 'admin';
  const canSeeFinance = hasPermission('ricavi', 'visualizza') || user?.ruolo === 'admin';

  // Export function for admin
  const handleExportBackup = async () => {
    setIsExporting(true);
    try {
      const response = await backupAPI.exportAll();
      const data = response.data;
      
      // Create filename with date
      const date = new Date().toISOString().split('T')[0];
      const filename = `gestionale_backup_${date}.json`;
      
      // Convert to JSON string and create blob
      const jsonString = JSON.stringify(data, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      
      // Create download link and trigger
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Export completato', {
        description: `File ${filename} scaricato con successo`
      });
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Errore durante l\'export', {
        description: error.response?.data?.detail || 'Si è verificato un errore'
      });
    } finally {
      setIsExporting(false);
    }
  };

  useEffect(() => {
    if (!isRisorsa) {
      loadDashboardData();
    }
  }, [isRisorsa]);

  const loadDashboardData = async () => {
    try {
      const promises = [
        dashboardAPI.getStats(),
        commesseAPI.getAll()
      ];
      
      if (canSeeFinance) {
        promises.push(fattureAPI.getSummary());
        promises.push(fattureAPI.getAlertScadenze(14));
      }
      
      const results = await Promise.all(promises);
      setStats(results[0].data);
      setCommesse(results[1].data);
      
      if (canSeeFinance && results[2]) {
        setRicaviSummary(results[2].data);
      }
      if (canSeeFinance && results[3]) {
        setAlertScadenze(results[3].data);
      }
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isRisorsa) {
    return <RisorsaDashboard />;
  }

  if (isLoading) {
    return (
      <div className="space-y-6" data-testid="dashboard-loading">
        <Skeleton className="h-12 w-64" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-28" />)}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Skeleton className="h-80 lg:col-span-2" />
          <Skeleton className="h-80" />
        </div>
      </div>
    );
  }

  // Calcola statistiche
  const commesseAttive = commesse.filter(c => c.stato === 'attiva');
  const budgetTotale = commesseAttive.reduce((sum, c) => sum + (c.budget_contratto || 0), 0);
  
  // Calcola marginalità complessiva su budget (totale costi vs totale budget)
  // Formula: (Budget Totale - Costi Totali) / Budget Totale * 100
  // Costi totali = somma di personale + trasferte + esterni per tutte le commesse attive
  const costiTotaliPortfolio = commesseAttive.reduce((sum, c) => sum + (c.kpi?.costi_totali_consuntivo || 0), 0);
  const marginalitaTotale = budgetTotale > 0 
    ? ((budgetTotale - costiTotaliPortfolio) / budgetTotale * 100)
    : 0;

  // Dati per grafico
  const chartData = commesseAttive
    .slice(0, 6)
    .map(c => ({
      name: c.codice_commessa,
      budget: c.budget_contratto || 0,
      costi: c.kpi?.costi_totali_consuntivo || 0
    }));

  // Alert count
  const alertCount = (alertScadenze?.scadute?.length || 0) + (alertScadenze?.in_scadenza?.length || 0);

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <p className="text-sm text-muted-foreground">Dashboard</p>
          <h1 className="text-3xl font-bold tracking-tight">Overview</h1>
        </div>
        <div className="flex gap-2">
          {/* Export Backup Button - Admin Only */}
          {isAdmin && (
            <Button 
              variant="outline" 
              className="rounded-sm"
              onClick={handleExportBackup}
              disabled={isExporting}
              data-testid="export-backup-btn"
            >
              {isExporting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              {isExporting ? 'Esportazione...' : 'Export Backup'}
            </Button>
          )}
          <Link to="/commesse">
            <Button variant="outline" className="rounded-sm">
              Vedi Commesse
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </Link>
          {canSeeFinance && (
            <Link to="/ricavi">
              <Button className="bg-blue-600 hover:bg-blue-700 rounded-sm">
                <Euro className="h-4 w-4 mr-2" />
                Ricavi
              </Button>
            </Link>
          )}
        </div>
      </div>

      {/* Alert Banner */}
      {alertCount > 0 && (
        <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-sm p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-sm bg-amber-100 dark:bg-amber-900">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-amber-800 dark:text-amber-200">
                {alertCount} fatture richiedono attenzione
              </p>
              <p className="text-sm text-amber-700 dark:text-amber-300">
                {alertScadenze?.scadute?.length || 0} scadute · {alertScadenze?.in_scadenza?.length || 0} in scadenza
              </p>
            </div>
            <Link to="/ricavi">
              <Button variant="outline" size="sm" className="border-amber-300 text-amber-700 hover:bg-amber-100">
                Gestisci
                <ArrowRight className="h-3 w-3 ml-1" />
              </Button>
            </Link>
          </div>
        </div>
      )}

      {/* Stats Grid - Bento Style */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Commesse Attive"
          value={commesseAttive.length}
          change={`${stats?.totale_commesse || 0} totali`}
          icon={Briefcase}
          accent
        />
        <StatCard
          label="Budget Attivo"
          value={formatCurrency(budgetTotale)}
          icon={Euro}
        />
        <StatCard
          label="Marginalità"
          value={`${marginalitaTotale >= 0 ? '+' : ''}${marginalitaTotale.toFixed(1)}%`}
          changeType={marginalitaTotale >= 0 ? 'positive' : 'negative'}
          change={marginalitaTotale >= 20 ? 'Ottimo' : marginalitaTotale >= 0 ? 'Nella norma' : 'Attenzione'}
          icon={TrendingUp}
          valueColor={marginalitaTotale >= 0 ? 'success' : 'danger'}
        />
        <StatCard
          label="Risorse"
          value={stats?.totale_risorse || 0}
          change={`${stats?.totale_clienti || 0} clienti`}
          icon={Users}
        />
      </div>

      {/* Finance Row (if has permission) */}
      {canSeeFinance && ricaviSummary && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <MetricCard
            title="Fatturato"
            value={formatCurrency(ricaviSummary.totale_fatturato)}
            subtitle={`${ricaviSummary.fatture_emesse} fatture emesse`}
            icon={FileText}
          />
          <MetricCard
            title="Incassato"
            value={formatCurrency(ricaviSummary.totale_incassato)}
            subtitle={`${ricaviSummary.fatture_pagate} fatture pagate`}
            icon={CheckCircle2}
            color="success"
          />
          <MetricCard
            title="Da Incassare"
            value={formatCurrency(ricaviSummary.totale_da_incassare)}
            subtitle={ricaviSummary.totale_in_ritardo > 0 ? `${formatCurrency(ricaviSummary.totale_in_ritardo)} in ritardo` : 'Nessun ritardo'}
            icon={Clock}
            color={ricaviSummary.totale_in_ritardo > 0 ? 'warning' : 'default'}
          />
        </div>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Chart */}
        <Card className="lg:col-span-2 rounded-sm">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Budget vs Costi</CardTitle>
              <Badge variant="outline" className="font-mono text-xs">Top 6 commesse</Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} barGap={2}>
                  <XAxis 
                    dataKey="name" 
                    tick={{ fontSize: 11, fontFamily: 'JetBrains Mono, monospace' }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis 
                    tick={{ fontSize: 11, fontFamily: 'JetBrains Mono, monospace' }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(v) => `${(v/1000).toFixed(0)}k`}
                  />
                  <Tooltip 
                    formatter={(value) => formatCurrency(value)}
                    contentStyle={{ 
                      background: 'hsl(var(--card))', 
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '2px',
                      fontSize: '12px'
                    }}
                  />
                  <Bar dataKey="budget" fill={COLORS.primary} radius={[2, 2, 0, 0]} name="Budget" />
                  <Bar dataKey="costi" fill={COLORS.muted} radius={[2, 2, 0, 0]} name="Costi" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card className="rounded-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Zap className="h-4 w-4 text-blue-600" />
              Azioni Rapide
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <Link to="/commesse" className="block">
              <Button variant="outline" className="w-full justify-start rounded-sm h-12">
                <Briefcase className="h-4 w-4 mr-3 text-blue-600" />
                <div className="text-left">
                  <p className="font-medium">Nuova Commessa</p>
                  <p className="text-xs text-muted-foreground">Crea un nuovo progetto</p>
                </div>
              </Button>
            </Link>
            <Link to="/timesheet/approvazione" className="block">
              <Button variant="outline" className="w-full justify-start rounded-sm h-12">
                <Clock className="h-4 w-4 mr-3 text-amber-600" />
                <div className="text-left">
                  <p className="font-medium">Approva Timesheet</p>
                  <p className="text-xs text-muted-foreground">Gestisci le ore</p>
                </div>
              </Button>
            </Link>
            {canSeeFinance && (
              <Link to="/ricavi" className="block">
                <Button variant="outline" className="w-full justify-start rounded-sm h-12">
                  <FileText className="h-4 w-4 mr-3 text-emerald-600" />
                  <div className="text-left">
                    <p className="font-medium">Nuova Fattura</p>
                    <p className="text-xs text-muted-foreground">Registra ricavo</p>
                  </div>
                </Button>
              </Link>
            )}
            <Link to="/costi-esterni" className="block">
              <Button variant="outline" className="w-full justify-start rounded-sm h-12">
                <Euro className="h-4 w-4 mr-3 text-purple-600" />
                <div className="text-left">
                  <p className="font-medium">Registra Costo</p>
                  <p className="text-xs text-muted-foreground">Aggiungi spesa</p>
                </div>
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>

      {/* Projects List */}
      <Card className="rounded-sm">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Commesse Attive</CardTitle>
            <Link to="/commesse">
              <Button variant="ghost" size="sm">
                Vedi tutte
                <ArrowRight className="h-3 w-3 ml-1" />
              </Button>
            </Link>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y">
            {commesseAttive.slice(0, 5).map(project => (
              <ProjectRow key={project.id} project={project} />
            ))}
            {commesseAttive.length === 0 && (
              <div className="p-8 text-center text-muted-foreground">
                <Briefcase className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>Nessuna commessa attiva</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardPage;
